import { Button, Card, Col, Descriptions, Row, Table, Tag, Typography, Statistic, DatePicker, Space, Modal, InputNumber, Input } from "antd";
import { EditOutlined, DeleteOutlined, ReloadOutlined, CalendarOutlined, PlusOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageTitle from "../page-header/PageHeader";
import { loadWorkerById, deleteWorker, reactivateWorker, addSalaryRevision } from "../../redux/actions/hr/workerActions";
import { getAllMachines } from "../../api/machineApi";
import Loader from "../loader/loader";
import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const { RangePicker } = DatePicker;

const WorkerDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const worker = useSelector((s) => s.workers.worker);
  const [machineMap, setMachineMap] = useState({});
  const [dateRange, setDateRange] = useState([dayjs().startOf("month"), dayjs()]);

  // Salary revision state
  const [revisionModal, setRevisionModal] = useState(false);
  const [revSalary, setRevSalary] = useState(null);
  const [revRate, setRevRate] = useState(null);
  const [revDate, setRevDate] = useState(null);
  const [revNote, setRevNote] = useState("");
  const [revLoading, setRevLoading] = useState(false);

  // Deactivate / Reactivate state
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [activateModal, setActivateModal] = useState(false);
  const [statusDate, setStatusDate] = useState(moment());
  const [statusNote, setStatusNote] = useState("");
  const [statusLoading, setStatusLoading] = useState(false);

  const fetchWorker = useCallback((range) => {
    const params = {};
    if (range && range[0] && range[1]) {
      params.from = range[0].format("YYYY-MM-DD");
      params.to = range[1].format("YYYY-MM-DD");
    }
    dispatch(loadWorkerById(id, params));
  }, [id, dispatch]);

  useEffect(() => { fetchWorker(dateRange); }, [id]);
  useEffect(() => {
    getAllMachines({ query: "all" }).then((res) => {
      const list = Array.isArray(res) ? res : res?.getAllMachine || [];
      const map = {};
      list.forEach((m) => { map[m.id] = `${m.name} (${m.code})`; });
      setMachineMap(map);
    }).catch(() => {});
  }, []);

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    fetchWorker(dates);
  };

  const handleQuickRange = (months) => {
    const to = dayjs();
    const from = dayjs().subtract(months, "month").startOf("month");
    const range = [from, to];
    setDateRange(range);
    fetchWorker(range);
  };

  const handleThisMonth = () => {
    const from = dayjs().startOf("month");
    const to = dayjs();
    const range = [from, to];
    setDateRange(range);
    fetchWorker(range);
  };

  const handleLastMonth = () => {
    const from = dayjs().subtract(1, "month").startOf("month");
    const to = dayjs().subtract(1, "month").endOf("month");
    const range = [from, to];
    setDateRange(range);
    fetchWorker(range);
  };

  const handleDeactivate = async () => {
    setStatusLoading(true);
    await dispatch(deleteWorker(id, statusDate?.toISOString(), statusNote));
    setStatusLoading(false);
    setDeactivateModal(false);
    setStatusDate(dayjs());
    setStatusNote("");
    fetchWorker(dateRange);
  };

  const handleReactivate = async () => {
    setStatusLoading(true);
    await dispatch(reactivateWorker(id, statusDate?.toISOString(), statusNote));
    setStatusLoading(false);
    setActivateModal(false);
    setStatusDate(dayjs());
    setStatusNote("");
    fetchWorker(dateRange);
  };

  if (!worker) return <Loader />;

  const attColumns = [
    {
      title: "Date", dataIndex: "date", key: "date",
      render: (v) => {
        const d = dayjs(v);
        const isSun = d.day() === 0;
        return <>{d.format("DD-MMM (ddd)")}{isSun && <Tag color="purple" style={{ marginLeft: 4, fontSize: 10 }}>SUN</Tag>}</>;
      },
    },
    { title: "Status", dataIndex: "status", key: "status", render: (v) => <Tag color={v === "present" ? "green" : v === "absent" ? "red" : "orange"}>{v}</Tag> },
    { title: "In", dataIndex: "in_time", key: "in_time", render: (v) => v ? dayjs.utc(v).format("hh:mm A") : "-" },
    { title: "Out", dataIndex: "out_time", key: "out_time", render: (v) => v ? dayjs.utc(v).format("hh:mm A") : "-" },
    { title: "Hrs", dataIndex: "total_hours", key: "total_hours" },
    { title: "OT", dataIndex: "overtime_hours", key: "overtime_hours", render: (v) => worker.salary_type === "hourly" ? "-" : v },
    {
      title: "EL", key: "el", width: 50,
      render: (_, r) => {
        const isSun = dayjs(r.date).day() === 0;
        if (!isSun) return "-";
        if (worker.salary_type !== "monthly") return "-";
        if (r.status === "absent") return <Tag color="red">No</Tag>;
        return <Tag color="cyan">Yes</Tag>;
      },
    },
  ];

  const advColumns = [
    { title: "Date", dataIndex: "date", key: "date", render: (v) => dayjs(v).format("DD-MMM-YYYY") },
    { title: "Amount", dataIndex: "amount", key: "amount", render: (v) => `₹${v.toLocaleString()}` },
    { title: "Status", dataIndex: "status", key: "status", render: (v) => <Tag>{v}</Tag> },
    { title: "Note", dataIndex: "note", key: "note" },
  ];

  return (
    <>
      <PageTitle title="Back" />
      <Card
        title={`Worker: ${worker.name}`}
        extra={
          <>
            <Button icon={<ReloadOutlined />} className="m-1" onClick={() => fetchWorker(dateRange)}>Refresh</Button>
            <Link to={`/hr/workers/${id}/update`} state={{ data: worker }}>
              <Button type="primary" icon={<EditOutlined />} className="m-1">Edit</Button>
            </Link>
            {worker.status ? (
              <Button danger icon={<DeleteOutlined />} className="m-1" onClick={() => { setStatusDate(moment()); setStatusNote(""); setDeactivateModal(true); }}>
                Deactivate
              </Button>
            ) : (
              <Button type="primary" style={{ background: "#52c41a", borderColor: "#52c41a" }} icon={<CheckCircleOutlined />} className="m-1" onClick={() => { setStatusDate(moment()); setStatusNote(""); setActivateModal(true); }}>
                Activate
              </Button>
            )}
          </>
        }
      >
        <Descriptions bordered column={{ xs: 1, md: 2 }} size="small">
          <Descriptions.Item label="Phone">{worker.phone || "N/A"}</Descriptions.Item>
          <Descriptions.Item label="ID No">{worker.id_no || "N/A"}</Descriptions.Item>
          <Descriptions.Item label="Department">{worker.department || "N/A"}</Descriptions.Item>
          <Descriptions.Item label="Designation">{worker.designation || "N/A"}</Descriptions.Item>
          <Descriptions.Item label="Worker Type"><Tag>{worker.worker_type || "N/A"}</Tag></Descriptions.Item>
          <Descriptions.Item label="Salary Type"><Tag color={worker.salary_type === "monthly" ? "blue" : "green"}>{worker.salary_type}</Tag></Descriptions.Item>
          <Descriptions.Item label="Rate">
            {worker.salary_type === "monthly" ? `₹${worker.monthly_salary?.toLocaleString()}/mo` : `₹${worker.hourly_rate}/hr`}
          </Descriptions.Item>
          <Descriptions.Item label="Shift Hours">{worker.shift_hours} hrs</Descriptions.Item>
          {worker.salary_type === "monthly" && (
            <Descriptions.Item label="Working Days">{worker.working_days === "mon-fri" ? "Mon–Fri (5 days)" : "Mon–Sat (6 days)"}</Descriptions.Item>
          )}
          <Descriptions.Item label="Join Date">{worker.join_date ? dayjs(worker.join_date).format("DD-MMM-YYYY") : "N/A"}</Descriptions.Item>
          {!worker.status && worker.inactive_from && (
            <Descriptions.Item label="Inactive From">
              <Tag color="red">{dayjs(worker.inactive_from).format("DD-MMM-YYYY")}</Tag>
            </Descriptions.Item>
          )}
          {worker.active_from && (
            <Descriptions.Item label="Last Activated">
              <Tag color="green">{dayjs(worker.active_from).format("DD-MMM-YYYY")}</Tag>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Machines Can Operate" span={2}>
            {worker.machine_ids?.length
              ? worker.machine_ids.map((mid) => <Tag key={mid} color="blue" style={{ marginBottom: 4 }}>{machineMap[mid] || mid}</Tag>)
              : "None assigned"}
          </Descriptions.Item>
          <Descriptions.Item label="Address" span={2}>{worker.address || "N/A"}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={24} md={14}>
          <Card
            title={
              <Space wrap>
                <CalendarOutlined />
                <span>Attendance {dateRange ? `(${dateRange[0].format("DD-MMM")} → ${dateRange[1].format("DD-MMM-YYYY")})` : `(01-${dayjs().format("MMM")} → ${dayjs().format("DD-MMM-YYYY")})`}</span>
              </Space>
            }
            extra={
              <Space wrap size="small">
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  size="small"
                  allowClear
                  style={{ width: 230 }}
                  needConfirm
                />
                <Button size="small" onClick={handleThisMonth} type={dateRange && dateRange[0]?.isSame(dayjs().startOf("month"), "day") ? "primary" : "default"}>This Month</Button>
                <Button size="small" onClick={handleLastMonth} type={dateRange && dateRange[0]?.isSame(dayjs().subtract(1, "month").startOf("month"), "day") && dateRange[1]?.isSame(dayjs().subtract(1, "month").endOf("month"), "day") ? "primary" : "default"}>Last Month</Button>
                <Button size="small" onClick={() => handleQuickRange(3)}>3 Mo</Button>
                <Button size="small" onClick={() => { setDateRange(null); fetchWorker(null); }}>Reset</Button>
              </Space>
            }
            size="small"
          >
            <Table dataSource={worker.attendance || []} columns={attColumns} rowKey="id" size="small" pagination={false} />
          </Card>
        </Col>
        <Col xs={24} md={10}>
          <Card title={`Advances (${(worker.advances || []).filter(a => a.status === "pending").length} pending)`} size="small">
            <Table dataSource={worker.advances || []} columns={advColumns} rowKey="id" size="small" pagination={false} />
          </Card>

          {/* ── Salary History ── */}
          <Card
            title="Salary History"
            size="small"
            style={{ marginTop: 16 }}
            extra={
              <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => {
                setRevSalary(worker.monthly_salary);
                setRevRate(worker.hourly_rate);
                setRevDate(dayjs());
                setRevNote("");
                setRevisionModal(true);
              }}>Add Revision</Button>
            }
          >
            <Table
              dataSource={worker.salary_history || []}
              rowKey="id"
              size="small"
              pagination={false}
              columns={[
                { title: "Effective From", dataIndex: "effective_from", key: "effective_from", render: (v) => dayjs(v).format("DD-MMM-YYYY") },
                { title: "Type", dataIndex: "salary_type", key: "salary_type", render: (v) => <Tag color={v === "monthly" ? "blue" : "green"}>{v}</Tag> },
                { title: "Monthly", dataIndex: "monthly_salary", key: "monthly_salary", render: (v) => v ? `₹${v.toLocaleString()}` : "-" },
                { title: "Hourly", dataIndex: "hourly_rate", key: "hourly_rate", render: (v) => v ? `₹${v}` : "-" },
                { title: "Note", dataIndex: "note", key: "note" },
                { title: "Date Added", dataIndex: "created_at", key: "created_at", render: (v) => dayjs(v).format("DD-MMM-YYYY") },
              ]}
            />
          </Card>

          <Modal
            title="Add Salary Revision"
            open={revisionModal}
            onCancel={() => setRevisionModal(false)}
            confirmLoading={revLoading}
            destroyOnClose
            onOk={async () => {
              if (!revDate) return;
              setRevLoading(true);
              const result = await dispatch(addSalaryRevision(id, {
                salary_type: worker.salary_type,
                monthly_salary: revSalary,
                hourly_rate: revRate,
                effective_from: revDate.format("YYYY-MM-DD"),
                note: revNote,
              }));
              setRevLoading(false);
              if (result) {
                setRevisionModal(false);
                fetchWorker(dateRange);
              }
            }}
          >
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <div>
                <Typography.Text strong>Effective From</Typography.Text>
                <DatePicker value={revDate} onChange={setRevDate} style={{ width: "100%" }} />
              </div>
              {worker.salary_type === "monthly" ? (
                <div>
                  <Typography.Text strong>Monthly Salary</Typography.Text>
                  <InputNumber value={revSalary} onChange={setRevSalary} style={{ width: "100%" }} prefix="₹" min={0} />
                </div>
              ) : (
                <div>
                  <Typography.Text strong>Hourly Rate</Typography.Text>
                  <InputNumber value={revRate} onChange={setRevRate} style={{ width: "100%" }} prefix="₹" min={0} />
                </div>
              )}
              <div>
                <Typography.Text strong>Note</Typography.Text>
                <Input value={revNote} onChange={(e) => setRevNote(e.target.value)} placeholder="Reason for revision" />
              </div>
            </Space>
          </Modal>
        </Col>
      </Row>

      {/* ── Due Summary ──────────────────────────────── */}
      {(() => {
        const allRecords = worker.salary_records || [];
        const settledRecords = allRecords.filter((r) => r.is_settled);
        const settledMonthKeys = new Set(settledRecords.map((r) => `${r.year}-${r.month}`));
        const att = worker.attendance || [];
        const adv = worker.advances || [];

        // Split attendance into settled months vs unsettled
        const unsettledAtt = att.filter((r) => {
          const d = dayjs(r.date);
          return !settledMonthKeys.has(`${d.year()}-${d.month() + 1}`);
        });

        // Render a settled summary card
        const renderSettled = (sr) => {
          const closing = +(sr.total_earnings - sr.total_advance - (sr.amount_paid || 0)).toFixed(2);
          return (
            <Card key={`settled-${sr.year}-${sr.month}`} title={<><Tag color="green">SETTLED</Tag> {dayjs(`${sr.year}-${sr.month}-01`).format("MMM YYYY")}</>} size="small" style={{ marginTop: 16 }}>
              <Row gutter={[16, 8]}>
                <Col xs={8} md={3}><Statistic title="Present" value={sr.present_days} valueStyle={{ fontSize: 18 }} /></Col>
                <Col xs={8} md={3}><Statistic title="Total Hrs" value={sr.total_hours?.toFixed(1)} valueStyle={{ fontSize: 18 }} /></Col>
                <Col xs={8} md={3}><Statistic title="OT Hrs" value={sr.overtime_hours?.toFixed(1)} valueStyle={{ fontSize: 18, color: "#fa541c" }} /></Col>
                <Col xs={8} md={3}><Statistic title="Gross" value={`₹${sr.gross_salary?.toLocaleString()}`} valueStyle={{ fontSize: 18 }} /></Col>
                <Col xs={8} md={3}><Statistic title="OT Amt" value={`₹${sr.overtime_amount?.toLocaleString()}`} valueStyle={{ fontSize: 18, color: "#fa541c" }} /></Col>
                <Col xs={8} md={3}><Statistic title="Total Earn" value={`₹${sr.total_earnings?.toLocaleString()}`} valueStyle={{ fontSize: 18 }} /></Col>
                <Col xs={8} md={3}><Statistic title="Advance" value={`₹${sr.total_advance?.toLocaleString()}`} valueStyle={{ fontSize: 18, color: "#cf1322" }} /></Col>
                <Col xs={8} md={3}><Statistic title="Net Pay" value={`₹${sr.net_payable?.toLocaleString()}`} valueStyle={{ fontSize: 18, color: sr.net_payable >= 0 ? "#3f8600" : "#cf1322" }} /></Col>
                <Col xs={8} md={3}><Statistic title="Amt Paid" value={`₹${(sr.amount_paid || 0).toLocaleString()}`} valueStyle={{ fontSize: 18, fontWeight: "bold" }} /></Col>
                <Col xs={8} md={3}>
                  <Statistic title="Closing Bal" value={`₹${closing.toLocaleString()}`} valueStyle={{ fontSize: 22, fontWeight: "bold", color: closing >= 0 ? "#3f8600" : "#cf1322" }} />
                </Col>
              </Row>
              {sr.settlement_note && <Typography.Text type="secondary" style={{ marginTop: 8, display: "block" }}>Note: {sr.settlement_note}</Typography.Text>}
            </Card>
          );
        };

        // Compute unsettled due from remaining attendance
        const renderUnsettled = () => {
          if (unsettledAtt.length === 0) return null;

          let presentDays = 0, totalHours = 0, otHours = 0;
          let sundayEarnedLeaves = 0;

          unsettledAtt.forEach((r) => {
            const isSunday = dayjs(r.date).day() === 0;
            if (isSunday) {
              if (r.status === "present" || r.status === "half_day") {
                sundayEarnedLeaves += 1;
              } else if (r.status !== "absent") {
                sundayEarnedLeaves += 1;
              }
            } else {
              if (r.status === "present") presentDays++;
              else if (r.status === "half_day") presentDays += 0.5;
            }
            totalHours += r.total_hours || 0;
            otHours += r.overtime_hours || 0;
          });
          presentDays += sundayEarnedLeaves;

          // Determine month for daily rate from unsettled attendance
          let monthDays;
          if (unsettledAtt.length > 0) {
            monthDays = dayjs(unsettledAtt[0].date).daysInMonth();
          } else {
            monthDays = dayjs().daysInMonth();
          }

          let gross = 0, hourlyRate = 0, otAmt = 0;
          if (worker.salary_type === "monthly") {
            const dailyRate = (worker.monthly_salary || 0) / monthDays;
            hourlyRate = dailyRate / (worker.shift_hours || 8);
            gross = +(dailyRate * presentDays).toFixed(2);
            otAmt = +(otHours * hourlyRate).toFixed(2);
          } else {
            hourlyRate = worker.hourly_rate || 0;
            const regularHrs = Math.max(0, totalHours - otHours);
            gross = +(regularHrs * hourlyRate).toFixed(2);
            otAmt = +(otHours * hourlyRate).toFixed(2);
          }
          const totalEarnings = +(gross + otAmt).toFixed(2);
          const totalAdv = adv.filter((a) => a.status === "pending").reduce((s, a) => s + a.amount, 0);
          const netDue = +(totalEarnings - totalAdv).toFixed(2);

          // Determine label for unsettled period
          const dates = unsettledAtt.map((r) => dayjs(r.date));
          const minDate = dates.reduce((a, b) => a.isBefore(b) ? a : b);
          const maxDate = dates.reduce((a, b) => a.isAfter(b) ? a : b);
          const label = minDate.format("DD-MMM") === maxDate.format("DD-MMM")
            ? minDate.format("DD-MMM-YYYY")
            : `${minDate.format("DD-MMM")} → ${maxDate.format("DD-MMM-YYYY")}`;

          return (
            <Card title={<><Tag color="orange">UNSETTLED</Tag> Due Summary ({label} • {unsettledAtt.length} records)</>} size="small" style={{ marginTop: 16 }}>
              <Row gutter={[16, 8]}>
                <Col xs={8} md={4}><Statistic title="Present Days" value={presentDays} valueStyle={{ fontSize: 18 }} /></Col>
                <Col xs={8} md={4}><Statistic title="Total Hrs" value={totalHours.toFixed(1)} valueStyle={{ fontSize: 18 }} /></Col>
                <Col xs={8} md={4}><Statistic title="OT Hrs" value={otHours.toFixed(1)} valueStyle={{ fontSize: 18, color: "#fa541c" }} /></Col>
                <Col xs={8} md={4}><Statistic title="Gross" value={`₹${gross.toLocaleString()}`} valueStyle={{ fontSize: 18 }} /></Col>
                <Col xs={8} md={4}><Statistic title="OT Amount" value={`₹${otAmt.toLocaleString()}`} valueStyle={{ fontSize: 18, color: "#fa541c" }} /></Col>
                <Col xs={8} md={4}><Statistic title="Total Earn" value={`₹${totalEarnings.toLocaleString()}`} valueStyle={{ fontSize: 18 }} /></Col>
                <Col xs={8} md={4}><Statistic title="Advance" value={`₹${totalAdv.toLocaleString()}`} valueStyle={{ fontSize: 18, color: "#cf1322" }} /></Col>
                <Col xs={12} md={4}>
                  <Statistic title="Net Due" value={`₹${netDue.toLocaleString()}`} valueStyle={{ fontSize: 22, fontWeight: "bold", color: netDue >= 0 ? "#3f8600" : "#cf1322" }} />
                </Col>
              </Row>
            </Card>
          );
        };

        // If no settled records at all, show simple unsettled summary for all attendance
        if (settledRecords.length === 0) {
          // Use all attendance as unsettled (unsettledAtt === att in this case)
          return renderUnsettled();
        }

        return (
          <>
            {settledRecords
              .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month)
              .map(renderSettled)}
            {renderUnsettled()}
          </>
        );
      })()}

      {/* Deactivate Modal */}
      <Modal
        title="Deactivate Worker"
        open={deactivateModal}
        onCancel={() => setDeactivateModal(false)}
        onOk={handleDeactivate}
        okText="Deactivate"
        okButtonProps={{ danger: true, loading: statusLoading }}
        destroyOnClose
      >
        <p>Worker will not appear in attendance from this date onward. Past attendance and ledger records remain unchanged.</p>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div>
            <label style={{ fontWeight: 500 }}>Effective Date (inactive from):</label>
            <DatePicker
              value={statusDate}
              onChange={setStatusDate}
              style={{ width: "100%", marginTop: 4 }}
              format="DD-MM-YYYY"
            />
          </div>
          <div>
            <label style={{ fontWeight: 500 }}>Note (optional):</label>
            <Input.TextArea
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              rows={2}
              placeholder="Reason for deactivation..."
              style={{ marginTop: 4 }}
            />
          </div>
        </Space>
      </Modal>

      {/* Reactivate Modal */}
      <Modal
        title="Reactivate Worker"
        open={activateModal}
        onCancel={() => setActivateModal(false)}
        onOk={handleReactivate}
        okText="Activate"
        okButtonProps={{ loading: statusLoading }}
        destroyOnClose
      >
        <p>Worker will start appearing in attendance from this date onward.</p>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div>
            <label style={{ fontWeight: 500 }}>Effective Date (active from):</label>
            <DatePicker
              value={statusDate}
              onChange={setStatusDate}
              style={{ width: "100%", marginTop: 4 }}
              format="DD-MM-YYYY"
            />
          </div>
          <div>
            <label style={{ fontWeight: 500 }}>Note (optional):</label>
            <Input.TextArea
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              rows={2}
              placeholder="Reason for reactivation..."
              style={{ marginTop: 4 }}
            />
          </div>
        </Space>
      </Modal>
    </>
  );
};

export default WorkerDetail;
